import { create } from "zustand"

import type { ProductInventoryPalletsSearchParams } from "../schemas"

import {
  GetProductInventoryByPalletDocument,
  type GetProductInventoryByPalletQuery,
  type InventoryByPalletItem,
} from "@/graphql/codegen/graphql"
import { createSearchStore } from "@/stores/createSearchStore"

type ProductInventoryPalletsFilters = ProductInventoryPalletsSearchParams & { productId: number }

const deduplicateInventories = (
  newData: InventoryByPalletItem[],
  existingData: InventoryByPalletItem[]
): InventoryByPalletItem[] => {
  const seen = new Set(existingData.map((item) => item.referenceNbr))
  return [...existingData, ...newData.filter((item) => !seen.has(item.referenceNbr))]
}

export const useProductInventoryPalletsSearchStore = create(
  createSearchStore<InventoryByPalletItem, ProductInventoryPalletsFilters, GetProductInventoryByPalletQuery>({
    documentNode: GetProductInventoryByPalletDocument,
    deduplicateData: deduplicateInventories,
    getQueryInput: (filters: ProductInventoryPalletsFilters, offset: number, limit: number) => {
      const { field, order, query, productId, warehouseAreaId } = filters

      return {
        offset,
        limit,
        sort: field && order ? { field, order } : undefined,
        filter: {
          query,
          productId,
          warehouseId: warehouseAreaId,
        },
      }
    },
    getQueryVariables: (queryInput) => ({ input: queryInput }),
    extractDataFromResponse: (data) => ({
      __typename: data.inventory.listByPallet.__typename,
      data:
        data.inventory.listByPallet.__typename === "InventoryByPalletListSuccess"
          ? (data.inventory.listByPallet.items as InventoryByPalletItem[])
          : [],
      error:
        data.inventory.listByPallet.__typename === "InventoryByPalletListFailure"
          ? data.inventory.listByPallet.error
          : undefined,
    }),
    hasMoreData: (response, state) => response.data.length === state.limit,
  })
)
